/* eslint-disable @nx/enforce-module-boundaries */
import React, { useRef, useEffect, useState } from 'react';
import { Button, Card, Typography, Paper, Box } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { useNavigate } from 'react-router-dom';
import notificationgray from '../assets/icons/notificationgray.svg';
import arrowUp from '../assets/icons/arrow-up-right_Purple.svg';
import clock from '../assets/icons/clockgray.svg';
import notificationSoundFile from 'packages/novous_web/src/assets/sounds/notification.mp3'; // Import your sound file
import { formatDateTimeNotification, computeTimeDifference } from '../utils/helper';
import useAxios from '../api/useAxios';
import { API_ENDPOINTS } from '../api/endpoints';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import CallingModal from 'packages/novous_web/src/common/components/CallingModal';
import callIcon from '../assets/icons/phone-calling.svg';

// Define the Notification interface (now with optional fleetAdminId).
interface Notification {
  notificationId: string;
  readStatus: number;
  message: string;
  vehicleId: string;
  date_time: string;
  latitude: string;
  longitude: string;
  gpsSpeed: number;
  fleetAdminId?: string; // Added optional fleetAdminId property
}

interface HeaderProps {
  onClose: () => void;
  notificationCount: number;
  fleetId: string;
  fleetAdminId?: string;
  isMuted: boolean;
  onToggleMute: () => void;
}

const Header: React.FC<HeaderProps> = ({
  onClose,
  notificationCount,
  fleetId,
  fleetAdminId,
  isMuted,
  onToggleMute,
}) => {
  const navigate = useNavigate();

  const handleViewAllClick = () => {
    // Pass both fleetId and fleetAdminId in the navigation state.
    navigate('/notifications', { state: { fleetId, fleetAdminId } });
    onClose();
  };

  return (
    <div className="flex justify-between items-center py-1">
      <div className="flex items-center gap-[10px]">
        <div
          className="w-8 h-8 rounded-lg p-1 flex items-center justify-center"
          style={{ background: '#EAE9F5' }}
        >
          <img src={notificationgray} alt="notification" className="w-4 h-4" />
        </div>
        <Typography
          component="span"
          sx={(theme) => ({
            fontSize: { xs: '16px', sm: '18px' },
            fontWeight: 600,
            color: theme.palette.mode === 'dark' ? '#fff' : '#000',
          })}
        >
          Notification ({notificationCount})
        </Typography>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="text"
          size="small"
          onClick={onToggleMute}
          sx={{
            minWidth: 32,
            padding: '4px',
            borderRadius: '8px',
            '&:hover': { backgroundColor: 'rgba(85, 71, 221, 0.04)' },
          }}
        >
          {isMuted ? (
            <VolumeOffIcon sx={{ fontSize: 20, color: '#5547DD' }} />
          ) : (
            <VolumeUpIcon sx={{ fontSize: 20, color: '#5547DD' }} />
          )}
        </Button>
        <Button
          variant="contained"
          size="small"
          sx={{
            backgroundColor: '#5547dd',
            textTransform: 'none',
            padding: { xs: '6px 12px', sm: '8px 16px' },
            borderRadius: '8px',
            '&:hover': { backgroundColor: '#4538c4' },
          }}
          onClick={handleViewAllClick}
          endIcon={
            <img
              src={arrowUp}
              alt="arrow"
              className="w-4 h-4"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          }
        >
          <Typography
            component="span"
            sx={{
              fontSize: { xs: '10px', sm: '12px' },
              fontWeight: 700,
              color: '#fff',
            }}
          >
            View all
          </Typography>
        </Button>
      </div>
    </div>
  );
};

interface NotificationItemProps {
  heading: string;
  description: React.ReactNode;
  time: string;
  isUnread?: boolean;
  onClick?: () => void;
  onCall?: () => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  heading,
  description,
  time,
  isUnread = false,
  onClick,
  onCall,
}) => (
  <Card
    onClick={onClick}
    className="p-4 rounded-xl border relative"
    sx={{
      backgroundColor: isUnread ? '#F0EFF8' : '#fff',
      borderColor: isUnread ? '#eeedfc' : '#f0eff8',
    }}
  >
    <div className="flex justify-between items-center">
      <Typography
        component="h3"
        sx={{
          fontSize: { xs: '14px', sm: '16px' },
          fontWeight: 600,
          margin: 0,
          color: '#000',
        }}
      >
        {heading}
      </Typography>
      <Box className="flex items-center gap-2">
        {onCall && (
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onCall();
            }}
            sx={{
              textTransform: 'none',
              padding: '4px 8px',
              borderRadius: '8px',
              borderColor: '#5547DD',
              color: '#5547DD',
              '&:hover': { borderColor: '#4538c4' },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <img src={callIcon} alt="call" width={14} height={14} />
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Call
            </Typography>
          </Button>
        )}
        {isUnread && (
          <StarIcon
            sx={{
              fontSize: 30,
              color: '#5547DD',
              border: '1px solid #5547DD',
              borderRadius: '8px',
              padding: '4px',
            }}
          />
        )}
      </Box>
    </div>
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        marginTop: 1,
        color: '#000',
      }}
    >
      {description}
    </Box>
    <div className="flex items-center gap-1 mt-2">
      <img src={clock} alt="time" className="w-4 h-4" />
      <Typography
        component="span"
        sx={{
          fontSize: { xs: '12px', sm: '14px' },
          color: '#000',
        }}
      >
        {time}
      </Typography>
    </div>
  </Card>
);

interface NotificationLayoutProps {
  onClose: () => void;
  notifications: Notification[];
  lastMessage?: string;
  fleetId: string; // Added fleetId prop
  isMuted: boolean; // ← add these two
  onToggleMute: () => void;
}

const NotificationLayout: React.FC<NotificationLayoutProps> = ({
  onClose,
  notifications: initialNotifications,
  lastMessage,
  fleetId,
  isMuted,
  onToggleMute,
}) => {
  const navigate = useNavigate();
  const [callingModalOpen, setCallingModalOpen] = useState(false);
  const [selectedTripInfo, setSelectedTripInfo] = useState<any>(null);
  // Initialize notifications from localStorage (if available) or from initialNotifications.
  const [notifications, setNotifications] = useState<Notification[]>(() => {
    const stored = localStorage.getItem('notifications');
    return stored ? JSON.parse(stored) : initialNotifications;
  });
  const layoutRef = useRef<HTMLDivElement>(null);

  // Create an Audio instance for the notification sound.
  const audioRef = useRef<HTMLAudioElement>(new Audio(notificationSoundFile));

  // Track the previous notifications count to detect new notifications.
  const [prevCount, setPrevCount] = useState<number>(notifications.length);

  // Persist notifications to localStorage whenever they change.
  useEffect(() => {
    localStorage.setItem('notifications', JSON.stringify(notifications));
  }, [notifications]);

  // Merge new notifications while preserving readStatus of existing ones.
  useEffect(() => {
    setNotifications((prev: Notification[]) => {
      const merged = [...prev];
      initialNotifications.forEach((notif) => {
        const index = merged.findIndex((n) => n.notificationId === notif.notificationId);
        if (index === -1) {
          merged.push(notif);
        } else {
          merged[index] = { ...notif, readStatus: merged[index].readStatus };
        }
      });
      // Sort notifications so that the latest (by date_time) appears first.
      merged.sort((a, b) => new Date(b.date_time).getTime() - new Date(a.date_time).getTime());
      return merged;
    });
  }, [initialNotifications]);

  // Play the notification sound when the notification count increases.
  useEffect(() => {
    // 1a) Always push React’s isMuted into the Audio element…
    audioRef.current.muted = isMuted;
    // 1b) …and also zero‐out its volume when muted
    audioRef.current.volume = isMuted ? 0 : 1;

    // 2) If the notification count just went up, try to play
    if (notifications.length > prevCount) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;

      // LOGGING for debug
      console.log(
        '[🔇 mute?]',
        audioRef.current.muted,
        '[🔊 vol]',
        audioRef.current.volume,
        '[▶️ play?]',
        !audioRef.current.muted,
      );

      if (!audioRef.current.muted) {
        audioRef.current.play().catch((err) => {
          console.warn('play() failed:', err);
        });
      }
    }

    // 3) Always bump prevCount at the end
    setPrevCount(notifications.length);
  }, [notifications.length, isMuted]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const notificationIcon = document.querySelector('[data-notification-icon]');
      if (
        layoutRef.current &&
        !layoutRef.current.contains(event.target as Node) &&
        !notificationIcon?.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  useEffect(() => {
    if (lastMessage) {
      // console.log(
      //   "Received message TopNav (from NotificationLayout):",
      //   lastMessage
      // );
    }
  }, [lastMessage]);

  // Destructure useAxios hook for the MARK_AS_READ endpoint.
  const {
    data: markAsReadData,
    error: markAsReadError,
    loading: markAsReadLoading,
    fetchData: fetchMarkAsReadData,
  } = useAxios<any>(API_ENDPOINTS.notification.MARK_AS_READ, 'POST');

  // Function to mark a notification as read by updating its readStatus to 1.
  const markAsRead = (index: number) => {
    const notificationToMark = notifications[index];

    // Call the MARK_AS_READ API with the required payload.
    fetchMarkAsReadData({
      notificationId: notificationToMark.notificationId,
      fleetId: fleetId, // using fleetId from props
      date_time: notificationToMark.date_time,
    });

    // Update local state to mark the notification as read.
    setNotifications((prevNotifications: Notification[]) => {
      const updated = [...prevNotifications];
      updated[index] = { ...updated[index], readStatus: 1 };
      return updated;
    });
  };

  // Assume fleetAdminId is available on at least one notification.
  const fleetAdminId = notifications[0]?.fleetAdminId;

  return (
    <Paper
      ref={layoutRef}
      elevation={3}
      className="rounded-lg p-4 flex flex-col"
      sx={{
        border: '1px solid #e0e0e0',
        width: { xs: '90%', sm: '468px' },
        maxHeight: { xs: '90vh', sm: '600px' },
        margin: 'auto',
      }}
    >
      <Header
        onClose={onClose}
        notificationCount={notifications.length}
        fleetId={fleetId}
        fleetAdminId={fleetAdminId}
        isMuted={isMuted}
        onToggleMute={onToggleMute}
      />
      <div className="mt-4 flex-1 overflow-y-auto space-y-4">
        {notifications.length > 0 ? (
          notifications.map((notification: Notification, index: number) => (
            <NotificationItem
              key={notification.notificationId || index}
              heading={notification.message || 'No message'}
              description={
                <>
                  <Typography variant="body2" sx={{ color: '#000' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700 }}>Vehicle ID: </span>
                    <span style={{ fontSize: '12px', fontWeight: 'normal' }}>{notification.vehicleId}</span>
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#000' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700 }}>Date Time: </span>
                    <span style={{ fontSize: '12px', fontWeight: 'normal' }}>
                      {formatDateTimeNotification(notification.date_time)}
                    </span>
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#000' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700 }}>Latitude: </span>
                    <span style={{ fontSize: '12px', fontWeight: 'normal' }}>{notification.latitude}</span>
                    {', '}
                    <span style={{ fontSize: '12px', fontWeight: 700 }}>Longitude: </span>
                    <span style={{ fontSize: '12px', fontWeight: 'normal' }}>{notification.longitude}</span>
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#000' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700 }}>GPS Speed: </span>
                    <span style={{ fontSize: '12px', fontWeight: 'normal' }}>
                      {notification.gpsSpeed} km/h
                    </span>
                  </Typography>
                </>
              }
              time={notification.date_time ? computeTimeDifference(notification.date_time) : ''}
              isUnread={notification.readStatus === 0}
              onClick={() => {
                // 1) mark this one as read
                markAsRead(index);
                // 2) navigate to your details page, passing the notification
                navigate('/alarm/vehicle-alert-details', { state: notification });
              }}
              onCall={() => {
                setSelectedTripInfo({
                  vehicleId: notification.vehicleId,
                  vehicleRegNo: notification.vehicleId,
                });
                setCallingModalOpen(true);
              }}
            />
          ))
        ) : (
          <Typography sx={{ textAlign: 'center', color: '#000' }}>No notifications</Typography>
        )}
      </div>
      <CallingModal
        open={callingModalOpen}
        onClose={() => setCallingModalOpen(false)}
        tripInfo={selectedTripInfo}
      />
    </Paper>
  );
};

export default NotificationLayout;
